import React from 'react';
import { useDesign } from '../../designs';

interface DesignButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  tier: 1 | 2 | 3 | 4;
  fullWidth?: boolean;
}

/**
 * Four-tier button hierarchy:
 * Tier 1: FAB — accent on elevated pill, floating action
 * Tier 2: Inline Primary — accent border/fill, main CTA
 * Tier 3: Secondary — accent text + optional diamond flanking
 * Tier 4: Tertiary — muted text, no decoration
 */
export default function DesignButton({
  tier,
  fullWidth = false,
  style,
  children,
  disabled,
  ...props
}: DesignButtonProps) {
  const design = useDesign();
  const { accent, textTertiary, bgElevated, borderEmphasis } = design.tokens;
  const fs = design.typography.fontSize;
  const { buttonAccentFlanking, cardBorderRadius } = design.options;

  const baseStyle: React.CSSProperties = {
    fontFamily: "'Raleway', sans-serif",
    fontSize: fs.button,
    fontWeight: 700,
    letterSpacing: '3px',
    textTransform: 'uppercase',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    width: fullWidth ? '100%' : undefined,
    transition: 'opacity 0.2s',
    lineHeight: 1,
  };

  let tierStyle: React.CSSProperties;

  switch (tier) {
    case 1: // FAB
      tierStyle = {
        background: bgElevated,
        border: `1px solid ${borderEmphasis}`,
        color: accent,
        borderRadius: '22px',
        padding: '11px 20px',
        boxShadow: '0 4px 14px rgba(10,6,18,0.4), 0 1.5px 5px rgba(0,0,0,0.2)',
      };
      break;
    case 2: // Inline Primary
      tierStyle = {
        background: 'transparent',
        border: `1px solid ${borderEmphasis}`,
        color: accent,
        borderRadius: `${cardBorderRadius}px`,
        padding: '11px 20px',
      };
      break;
    case 3: // Secondary
      tierStyle = {
        background: 'transparent',
        border: 'none',
        color: accent,
        borderRadius: 0,
        padding: '8px 4px',
      };
      break;
    case 4: // Tertiary
    default:
      tierStyle = {
        background: 'transparent',
        border: 'none',
        color: textTertiary,
        borderRadius: 0,
        padding: '8px 4px',
        fontFamily: "'Raleway', sans-serif",
        fontWeight: 300,
        letterSpacing: '0',
        textTransform: 'none',
        fontSize: fs.bodyInput,
      };
      break;
  }

  const showFlanking = buttonAccentFlanking && (tier === 2 || tier === 3);

  return (
    <button
      type="button"
      disabled={disabled}
      style={{ ...baseStyle, ...tierStyle, ...style }}
      {...props}
    >
      {showFlanking && <span style={{ marginRight: '6px', fontSize: '7px' }}>◆</span>}
      {children}
      {showFlanking && <span style={{ marginLeft: '6px', fontSize: '7px' }}>◆</span>}
    </button>
  );
}
